// src/screens/DealStatus.tsx
// Shows deals with their final status (interested, not interested, under contract, closed, dead)

import React, { useEffect, useState, useCallback } from 'react';
import {
  Button, Stack, Chip, Snackbar, Alert, TextField,
  FormControl, InputLabel, Select, MenuItem, Tabs, Tab,
  Dialog, DialogTitle, DialogContent, DialogActions,
  useMediaQuery, useTheme,
} from '@mui/material';
import { apiFetch, verify, getUsers } from '../helpers';

type SubadminOption = { id: string; name: string; states: string[] };

type Note = { note: string; createdAt: string };

type Deal = {
  _id: string;
  fullAddress?: string;
  address?: string;
  city?: string;
  state?: string;
  listingPrice?: number;
  amv?: number;
  agentName?: string;
  agentPhone?: string;
  agentEmail?: string;
  emailSentAt?: string;
  dealStage?: string;
  dealStatus?: string;
  naReason?: string;
  followUpDate?: string;
  followUpNotes?: Note[];
  offerAmount?: number;
  movedToDealStatusAt?: string;
  source?: string;
  _source?: string;  // Legacy — no longer used
  // AMV source tracking
  amvSource?: string;
  // Property details
  beds?: number;
  baths?: number;
  sqft?: number;
  yearBuilt?: number;
  homeType?: string;
  // Vendor valuations
  bofa_value?: number;
  redfin_value?: number;
  redfin_adjusted?: number;
  redfinAmv?: number;
  realtorAmv?: number;
  bofaAmv?: number;
  zestimate?: number;
  // Geo
  lat?: number;
  lng?: number;
};

const currency = new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const fmt = (n?: number | null) => (typeof n === 'number' && n > 0 ? currency.format(n) : '—');
const formatDateTime = (d?: string | null) => {
  if (!d) return '—';
  const date = new Date(d);
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' },
];

const STATUS_LABELS: Record<string, { label: string; color: string; bg: string }> = {
  offer_sent_out: { label: 'Offer Sent Out', color: '#2563eb', bg: '#dbeafe' },
  counter: { label: 'Counter', color: '#d97706', bg: '#fef3c7' },
  declined: { label: 'Declined', color: '#dc2626', bg: '#fef2f2' },
  under_contract: { label: 'Under Contract', color: '#7c3aed', bg: '#ede9fe' },
  closed: { label: 'Closed', color: '#16a34a', bg: '#dcfce7' },
  deal: { label: 'Deals', color: '#059669', bg: '#d1fae5' },
  not_applicable: { label: 'Not Applicable', color: '#6b7280', bg: '#f3f4f6' },
};

const NA_REASONS: Record<string, string> = {
  does_not_meet_criteria: 'Does not meet Criteria',
  auction: 'Auction',
  duplicate: 'Duplicate',
  mobile_manufactured: 'Mobile/Manufactured',
  condo_townhome: 'Condo/Townhome',
};

// AMV source display labels
const AMV_SOURCE_LABELS: Record<string, { label: string; color: string }> = {
  redfin: { label: 'Redfin', color: '#dc2626' },
  bofa: { label: 'BofA', color: '#dc2626' },
  zestimate: { label: 'Zestimate', color: '#2563eb' },
  realtor: { label: 'Realtor', color: '#7c3aed' },
};

// Source display config
const SOURCE_DISPLAY: Record<string, { label: string; color: string; bg: string; border: string }> = {
  privy: { label: 'Privy', color: '#7c3aed', bg: '#f5f3ff', border: '#7c3aed' },
  'privy-flip': { label: 'Privy-Flip', color: '#7c3aed', bg: '#f5f3ff', border: '#7c3aed' },
  'privy-Tear': { label: 'Privy-Tear', color: '#7c3aed', bg: '#f5f3ff', border: '#7c3aed' },
  redfin: { label: 'Redfin', color: '#dc2626', bg: '#fef2f2', border: '#dc2626' },
  zillow: { label: 'Zillow', color: '#2563eb', bg: '#eff6ff', border: '#2563eb' },
  hud: { label: 'HUD', color: '#0d9488', bg: '#f0fdfa', border: '#0d9488' },
  fanniemae: { label: 'Fannie Mae', color: '#2563eb', bg: '#eff6ff', border: '#2563eb' },
  rpr: { label: 'RPR', color: '#d97706', bg: '#fef3c7', border: '#d97706' },
};

// Convert address to Wells Fargo ComeHome URL
const addressToWellsFargoUrl = (address: string): string => {
  if (!address) return '';
  const abbreviations: Record<string, string> = {
    avenue: 'Ave', street: 'St', road: 'Rd', drive: 'Dr',
    boulevard: 'Blvd', lane: 'Ln', court: 'Ct', circle: 'Cir',
    place: 'Pl', parkway: 'Pkwy', highway: 'Hwy', terrace: 'Ter',
    trail: 'Trl', way: 'Way',
  };
  let a = address.trim();
  for (const [full, abbr] of Object.entries(abbreviations)) {
    a = a.replace(new RegExp(`\\b${full}\\b`, 'gi'), abbr);
  }
  const slug = a.replace(/,/g, '').replace(/\s+/g, '-').replace(/-+/g, '-');
  return `https://wellsfargo.comehome.com/property-details/${slug}`;
};

export default function DealStatusPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [deals, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('offer_sent_out');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterState, setFilterState] = useState<string>('all');
  const [filterSource, setFilterSource] = useState<string>('all');

  // Admin-only: "View as Subadmin" filter — see the page as a specific subadmin
  const [isAdmin, setIsAdmin] = useState(false);
  const [subadmins, setSubadmins] = useState<SubadminOption[]>([]);
  const [viewAsSubadmin, setViewAsSubadmin] = useState<string>('all');

  // Move dialog state
  const [moveDialog, setMoveDialog] = useState<{ open: boolean; deal: Deal | null; toStage: string }>({
    open: false, deal: null, toStage: ''
  });
  const [moveNote, setMoveNote] = useState('');
  const [followUpDate, setFollowUpDate] = useState('');
  const [moving, setMoving] = useState(false);

  // Delete dialog state
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; deal: Deal | null }>({
    open: false, deal: null
  });
  const [deleting, setDeleting] = useState(false);

  // Edit status dialog
  const [editDialog, setEditDialog] = useState<{ open: boolean; deal: Deal | null }>({ open: false, deal: null });
  const [newStatus, setNewStatus] = useState('');
  const [offerAmount, setOfferAmount] = useState('');
  const [editNote, setEditNote] = useState('');
  const [editAgentName, setEditAgentName] = useState('');
  const [editAgentPhone, setEditAgentPhone] = useState('');
  const [editAgentEmail, setEditAgentEmail] = useState('');
  const [editNaReason, setEditNaReason] = useState('');
  const [updating, setUpdating] = useState(false);

  // View notes dialog
  const [viewNotesDialog, setViewNotesDialog] = useState<{ open: boolean; deal: Deal | null }>({ open: false, deal: null });

  // Add note dialog
  const [noteDialog, setNoteDialog] = useState<{ open: boolean; deal: Deal | null }>({ open: false, deal: null });
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);

  const [toast, setToast] = useState<{ open: boolean; msg: string; sev: 'success' | 'error' }>({
    open: false, msg: '', sev: 'success'
  });

  // Expandable detail row
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [detailViewMode, setDetailViewMode] = useState<'street' | 'map'>('street');

  const GMAPS_KEY =
    (process.env.REACT_APP_GOOGLE_MAPS_GOOGLE_MAPS_KEY as string) ||
    (process.env.REACT_APP_GOOGLE_MAPS_KEY as string) ||
    (process.env.NEXT_PUBLIC_GOOGLE_MAPS_KEY as string) ||
    '';

  const toggleExpand = (id: string) => {
    setExpandedId(prev => prev === id ? null : id);
    setDetailViewMode('street'); // Reset to street view when opening
  };

  // Get source display info
  const getSourceInfo = (deal: Deal) => {
    const src = deal.source || '';
    if (src.startsWith('privy')) {
      return SOURCE_DISPLAY[src] || SOURCE_DISPLAY.privy;
    }
    return SOURCE_DISPLAY[src] || { label: src || 'Unknown', color: '#6b7280', bg: '#f3f4f6', border: '#9ca3af' };
  };

  // Get AMV source display
  const getAmvSourceInfo = (deal: Deal) => {
    const src = deal.amvSource;
    if (!src) return null;
    return AMV_SOURCE_LABELS[src] || { label: src, color: '#6b7280' };
  };

  // Debounce search - wait 300ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1); // Reset to page 1 when search changes
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const loadDeals = useCallback(async () => {
    try {
      setLoading(true);
      const searchParam = debouncedSearch ? `&search=${encodeURIComponent(debouncedSearch)}` : '';
      const stateParam = filterState !== 'all' ? `&state=${filterState}` : '';
      const sourceParam = filterSource !== 'all' ? `&source=${filterSource}` : '';
      const viewAsParam = isAdmin && viewAsSubadmin !== 'all' ? `&viewAsSubadmin=${viewAsSubadmin}` : '';
      const res = await apiFetch(`/api/deal-pipeline/stage/deal_status?page=${page}&limit=50&status=${statusFilter}${searchParam}${stateParam}${sourceParam}${viewAsParam}`);
      const data = await res.json();
      if (data.deals) {
        setDeals(data.deals);
        setTotalPages(data.pagination?.pages || 1);
        setTotal(data.pagination?.total || 0);
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load deals');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, debouncedSearch, filterState, filterSource, isAdmin, viewAsSubadmin]);

  // On mount: check if current user is admin; if so, load the subadmin list for the dropdown.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const v: any = await verify();
        const u = v?.user || v?.data || null;
        const admin = Boolean(u?.isAdmin || u?.role === 'admin' || u?.is_admin === true);
        if (cancelled) return;
        setIsAdmin(admin);
        if (!admin) return;

        const usersRes: any = await getUsers();
        if (cancelled || !usersRes?.ok) return;
        const list: any[] = Array.isArray(usersRes.data) ? usersRes.data : [];
        const subs: SubadminOption[] = list
          .filter((x) => x && !x.deleted_at && x.role !== 'admin' && x.is_admin !== true)
          .map((x) => ({
            id: String(x._id || x.id),
            name: x.full_name || x.email || 'Unnamed',
            states: Array.isArray(x.states) ? x.states.map((s: string) => String(s).toUpperCase()) : [],
          }))
          .sort((a, b) => a.name.localeCompare(b.name));
        setSubadmins(subs);
      } catch {
        /* non-admin or auth failure — leave dropdown hidden */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    loadDeals();
  }, [loadDeals]);

  const openMoveDialog = (deal: Deal, toStage: string) => {
    setMoveDialog({ open: true, deal, toStage });
    setMoveNote('');
    setFollowUpDate('');
  };

  const handleMove = async () => {
    if (!moveDialog.deal) return;
    setMoving(true);
    try {
      const body: any = {
        toStage: moveDialog.toStage,
        note: moveNote || undefined,
      };
      if (moveDialog.toStage === 'follow_up' && followUpDate) {
        body.followUpDate = followUpDate;
      }

      const res = await apiFetch(`/api/deal-pipeline/move/${moveDialog.deal._id}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success) {
        const stageLabels: Record<string, string> = {
          'email_sent': 'Email Sent',
          'follow_up': 'Follow Up'
        };
        setToast({ open: true, msg: `Moved to ${stageLabels[moveDialog.toStage] || moveDialog.toStage}`, sev: 'success' });
        setMoveDialog({ open: false, deal: null, toStage: '' });
        loadDeals();
      } else {
        setToast({ open: true, msg: data.error || 'Failed to move', sev: 'error' });
      }
    } catch (e: any) {
      setToast({ open: true, msg: e?.message || 'Failed to move', sev: 'error' });
    } finally {
      setMoving(false);
    }
  };

  const openDeleteDialog = (deal: Deal) => {
    setDeleteDialog({ open: true, deal });
  };

  const handleDelete = async () => {
    if (!deleteDialog.deal) return;
    setDeleting(true);
    try {
      const res = await apiFetch(`/api/scraped-deals/${deleteDialog.deal._id}`, {
        method: 'DELETE',
      });
      const data = await res.json();
      if (data.success || res.ok) {
        setToast({ open: true, msg: 'Deal deleted', sev: 'success' });
        setDeleteDialog({ open: false, deal: null });
        loadDeals();
      } else {
        setToast({ open: true, msg: data.error || 'Failed to delete', sev: 'error' });
      }
    } catch (e: any) {
      setToast({ open: true, msg: e?.message || 'Failed to delete', sev: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!editDialog.deal) return;
    setUpdating(true);
    try {
      const body: any = {};
      if (newStatus) body.dealStatus = newStatus;
      if (offerAmount) body.offerAmount = parseFloat(offerAmount.replace(/[^0-9.]/g, ''));
      if (editNote.trim()) body.note = editNote.trim();
      if (newStatus === 'not_applicable') body.naReason = editNaReason || null;
      // Include agent info
      body.agentName = editAgentName.trim();
      body.agentPhone = editAgentPhone.trim();
      body.agentEmail = editAgentEmail.trim();

      const res = await apiFetch(`/api/deal-pipeline/status/${editDialog.deal._id}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success) {
        setToast({ open: true, msg: 'Status updated', sev: 'success' });
        setEditDialog({ open: false, deal: null });
        loadDeals();
      } else {
        setToast({ open: true, msg: data.error || 'Failed to update', sev: 'error' });
      }
    } catch (e: any) {
      setToast({ open: true, msg: e?.message || 'Failed to update', sev: 'error' });
    } finally {
      setUpdating(false);
    }
  };

  const handleAddNote = async () => {
    if (!noteDialog.deal || !newNote.trim()) return;
    setAddingNote(true);
    try {
      const res = await apiFetch(`/api/deal-pipeline/note/${noteDialog.deal._id}`, {
        method: 'POST',
        body: JSON.stringify({ note: newNote.trim() }),
      });
      const data = await res.json();
      if (data.success) {
        setToast({ open: true, msg: 'Note added', sev: 'success' });
        setNoteDialog({ open: false, deal: null });
        setNewNote('');
        loadDeals();
      } else {
        setToast({ open: true, msg: data.error || 'Failed to add note', sev: 'error' });
      }
    } catch (e: any) {
      setToast({ open: true, msg: e?.message || 'Failed to add note', sev: 'error' });
    } finally {
      setAddingNote(false);
    }
  };

  // Quick status change directly from dropdown
  const handleQuickStatusChange = async (deal: Deal, newStatusValue: string) => {
    if (!deal || newStatusValue === deal.dealStatus) return;
    try {
      const res = await apiFetch(`/api/deal-pipeline/status/${deal._id}`, {
        method: 'PUT',
        body: JSON.stringify({ dealStatus: newStatusValue }),
      });
      const data = await res.json();
      if (data.success) {
        setToast({ open: true, msg: `Status changed to ${STATUS_LABELS[newStatusValue]?.label || newStatusValue}`, sev: 'success' });
        loadDeals();
      } else {
        setToast({ open: true, msg: data.error || 'Failed to update status', sev: 'error' });
      }
    } catch (e: any) {
      setToast({ open: true, msg: e?.message || 'Failed to update status', sev: 'error' });
    }
  };

  const handleNaReasonChange = async (deal: Deal, reason: string) => {
    if (!deal || reason === deal.naReason) return;
    try {
      const res = await apiFetch(`/api/deal-pipeline/status/${deal._id}`, {
        method: 'PUT',
        body: JSON.stringify({ naReason: reason }),
      });
      const data = await res.json();
      if (data.success) {
        setToast({ open: true, msg: `Reason set to ${NA_REASONS[reason] || reason}`, sev: 'success' });
        loadDeals();
      } else {
        setToast({ open: true, msg: data.error || 'Failed to update reason', sev: 'error' });
      }
    } catch (e: any) {
      setToast({ open: true, msg: e?.message || 'Failed to update reason', sev: 'error' });
    }
  };

  const getStatusChip = (status?: string | null) => {
    const s = status || 'offer_sent_out';
    const info = STATUS_LABELS[s] || STATUS_LABELS.offer_sent_out;
    return (
      <Chip
        label={info.label}
        size="small"
        sx={{
          backgroundColor: info.bg,
          color: info.color,
          fontWeight: 600,
          fontSize: 10,
        }}
      />
    );
  };

  // Calculate pricing values - ensure proper number conversion
  const toNum = (v: any): number | null => {
    if (v == null) return null;
    const n = typeof v === 'number' ? v : parseFloat(String(v).replace(/[^0-9.\-]/g, ''));
    return Number.isFinite(n) && n > 0 ? n : null;
  };

  const getLP = (deal: Deal) => toNum(deal.listingPrice);
  const getLP80 = (deal: Deal) => {
    const lp = getLP(deal);
    return lp ? Math.round(lp * 0.8) : null;
  };
  const getAMV = (deal: Deal) => toNum(deal.amv);
  const getAMV40 = (deal: Deal) => {
    const amv = getAMV(deal);
    return amv ? Math.round(amv * 0.4) : null;
  };
  const getAMV30 = (deal: Deal) => {
    const amv = getAMV(deal);
    return amv ? Math.round(amv * 0.3) : null;
  };
  const getOffer = (deal: Deal) => {
    const lp80 = getLP80(deal);
    const amv40 = getAMV40(deal);
    if (lp80 && amv40) return Math.min(lp80, amv40);
    return lp80 || amv40 || null;
  };

  const cellPadding = isMobile ? '8px 4px' : '10px 6px';
  const cellFontSize = isMobile ? '12px' : '13px';

  if (loading && deals.length === 0) return <div style={{ padding: 24 }}>Loading...</div>;
  if (error) return <div style={{ padding: 24, color: '#ef4444' }}>{error}</div>;

  return (
    <div style={{ padding: isMobile ? 12 : 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <h2 style={{ margin: 0, fontSize: isMobile ? 18 : 22, fontWeight: 800, color: '#111827' }}>
          Deal Status
        </h2>
        <Chip label={`${total} deals`} color="secondary" size="small" />
      </div>

      <p style={{ color: '#6b7280', marginBottom: 16, fontSize: 14 }}>
        Track the final outcome of your deals. Update status, add notes, and record offer amounts.
      </p>

      {/* Search Box & Filters */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16, alignItems: 'center' }}>
        <TextField
          placeholder="Search by address..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          sx={{
            minWidth: isMobile ? '100%' : 300,
            flex: isMobile ? '1 1 100%' : 'none',
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#fff',
              borderRadius: 2,
            }
          }}
          slotProps={{
            input: {
              startAdornment: (
                <span style={{ marginRight: 8, color: '#9ca3af' }}>🔍</span>
              ),
              endAdornment: searchQuery ? (
                <Button
                  size="small"
                  onClick={() => setSearchQuery('')}
                  sx={{ minWidth: 'auto', p: 0.5, color: '#6b7280' }}
                >
                  ✕
                </Button>
              ) : null,
            }
          }}
        />
        <FormControl size="small" sx={{ minWidth: isMobile ? 140 : 200 }}>
          <InputLabel>Filter by State</InputLabel>
          <Select
            value={filterState}
            label="Filter by State"
            onChange={(e) => { setFilterState(e.target.value); setPage(1); }}
            MenuProps={{ PaperProps: { sx: { maxHeight: 400 } } }}
          >
            <MenuItem value="all">All States</MenuItem>
            {US_STATES.map((s) => (
              <MenuItem key={s.code} value={s.code}>
                {s.code} - {s.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Filter by Source</InputLabel>
          <Select
            value={filterSource}
            label="Filter by Source"
            onChange={(e) => { setFilterSource(e.target.value); setPage(1); }}
          >
            <MenuItem value="all">All Sources</MenuItem>
            <MenuItem value="scraped">Privy</MenuItem>
            <MenuItem value="redfin-amv">Redfin</MenuItem>
            <MenuItem value="zillow">Zillow</MenuItem>
            <MenuItem value="hud">HUD</MenuItem>
            <MenuItem value="fanniemae">Fannie Mae</MenuItem>
            <MenuItem value="rpr">RPR</MenuItem>
            <MenuItem value="regions">Regions Bank</MenuItem>
            <MenuItem value="mtbank">M&T Bank</MenuItem>
            <MenuItem value="xome">Xome</MenuItem>
          </Select>
        </FormControl>
        {isAdmin && subadmins.length > 0 && (
          <FormControl
            size="small"
            sx={{
              minWidth: isMobile ? 160 : 220,
              '& .MuiOutlinedInput-root': {
                backgroundColor: viewAsSubadmin !== 'all' ? '#fef3c7' : undefined,
              },
            }}
          >
            <InputLabel>View as Subadmin</InputLabel>
            <Select
              value={viewAsSubadmin}
              label="View as Subadmin"
              onChange={(e) => { setViewAsSubadmin(e.target.value); setPage(1); }}
              MenuProps={{ PaperProps: { sx: { maxHeight: 400 } } }}
            >
              <MenuItem value="all">All deals (admin view)</MenuItem>
              {subadmins.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}{s.states.length ? ` — ${s.states.join(', ')}` : ' — no states'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {isAdmin && viewAsSubadmin !== 'all' && (
          <Chip
            label={`Viewing as: ${subadmins.find((s) => s.id === viewAsSubadmin)?.name || 'subadmin'}`}
            onDelete={() => { setViewAsSubadmin('all'); setPage(1); }}
            color="warning"
            size="small"
            sx={{ fontWeight: 600 }}
          />
        )}
      </div>

      {/* Status Filter Tabs */}
      <div style={{ marginBottom: 16, overflowX: 'auto' }}>
        <Tabs
          value={statusFilter}
          onChange={(_, v) => { setStatusFilter(v); setPage(1); }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minWidth: 100 },
          }}
        >
          <Tab value="offer_sent_out" label="Offer Sent Out" />
          <Tab value="counter" label="Counter" />
          <Tab value="declined" label="Declined" />
          <Tab value="under_contract" label="Under Contract" />
          <Tab value="closed" label="Closed" />
          <Tab value="deal" label="Deals" />
          <Tab value="not_applicable" label="Not Applicable" />
        </Tabs>
      </div>

      <div
        style={{
          overflowX: 'auto',
          borderRadius: 12,
          border: '1px solid #e5e7eb',
          background: '#fff',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, minWidth: 1400 }}>
          <thead>
            <tr style={{ background: '#111827', color: '#fff' }}>
              {[...['Address', 'LP', '80%', 'AMV', '40%', '30%', 'Offer', 'Agent', 'Contact', 'Moved At', 'Status'], ...(statusFilter === 'not_applicable' ? ['Reason'] : []), 'Notes', 'Edit'].map((h, i) => (
                <th
                  key={h}
                  style={{
                    textAlign: i === 0 || i === 7 || i === 8 ? 'left' : 'right',
                    padding: cellPadding,
                    fontSize: 11,
                    letterSpacing: 0.3,
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                    borderBottom: '1px solid rgba(255,255,255,0.12)',
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {deals.map((deal, i) => {
              const noteCount = deal.followUpNotes?.length || 0;
              const isExpanded = expandedId === deal._id;

              return (
                <React.Fragment key={deal._id}>
                <tr style={{ background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                  <td
                    style={{
                      padding: cellPadding,
                      fontSize: cellFontSize,
                      fontWeight: 600,
                      color: isExpanded ? '#2563eb' : '#111',
                      minWidth: 150,
                      cursor: 'pointer',
                      textDecoration: isExpanded ? 'underline' : 'none',
                    }}
                    onClick={() => toggleExpand(deal._id)}
                    title="Click to expand details"
                  >
                    {deal.fullAddress || deal.address || '—'}
                  </td>
                  <td style={{ padding: cellPadding, fontSize: cellFontSize, textAlign: 'right', whiteSpace: 'nowrap', color: '#374151' }}>
                    {fmt(getLP(deal))}
                  </td>
                  <td style={{ padding: cellPadding, fontSize: cellFontSize, textAlign: 'right', whiteSpace: 'nowrap', color: '#374151' }}>
                    {fmt(getLP80(deal))}
                  </td>
                  <td style={{ padding: cellPadding, fontSize: cellFontSize, textAlign: 'right', whiteSpace: 'nowrap', color: '#374151' }}>
                    {fmt(getAMV(deal))}
                  </td>
                  <td style={{ padding: cellPadding, fontSize: cellFontSize, textAlign: 'right', whiteSpace: 'nowrap', color: '#374151' }}>
                    {fmt(getAMV40(deal))}
                  </td>
                  <td style={{ padding: cellPadding, fontSize: cellFontSize, textAlign: 'right', whiteSpace: 'nowrap', color: '#374151' }}>
                    {fmt(getAMV30(deal))}
                  </td>
                  <td style={{ padding: cellPadding, fontSize: cellFontSize, textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 600, color: '#059669' }}>
                    {fmt(getOffer(deal))}
                  </td>
                  {/* Agent Name - read only */}
                  <td style={{ padding: cellPadding, fontSize: cellFontSize, color: '#374151' }}>
                    {deal.agentName || '—'}
                  </td>
                  {/* Agent Contact - read only */}
                  <td style={{ padding: cellPadding, fontSize: cellFontSize }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {deal.agentEmail && (
                        <a href={`mailto:${deal.agentEmail}`} style={{ color: '#2563eb', fontSize: 11 }}>{deal.agentEmail}</a>
                      )}
                      {deal.agentPhone && (
                        <a href={`tel:${deal.agentPhone}`} style={{ color: '#2563eb', fontSize: 11 }}>{deal.agentPhone}</a>
                      )}
                      {!deal.agentEmail && !deal.agentPhone && '—'}
                    </div>
                  </td>
                  <td style={{ padding: cellPadding, fontSize: cellFontSize, color: '#6b7280', whiteSpace: 'nowrap', textAlign: 'right' }}>
                    {formatDateTime(deal.movedToDealStatusAt || deal.emailSentAt)}
                  </td>
                  <td style={{ padding: cellPadding, fontSize: cellFontSize }}>
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                      <Select
                        value={deal.dealStatus || 'offer_sent_out'}
                        onChange={(e) => handleQuickStatusChange(deal, e.target.value)}
                        sx={{
                          fontSize: 11,
                          '& .MuiSelect-select': { py: 0.5, px: 1 },
                          backgroundColor: STATUS_LABELS[deal.dealStatus || 'offer_sent_out']?.bg || '#f3f4f6',
                          color: STATUS_LABELS[deal.dealStatus || 'offer_sent_out']?.color || '#6b7280',
                          fontWeight: 600,
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
                          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: STATUS_LABELS[deal.dealStatus || 'offer_sent_out']?.color || '#6b7280' },
                        }}
                      >
                        <MenuItem value="offer_sent_out">Offer Sent Out</MenuItem>
                        <MenuItem value="counter">Counter</MenuItem>
                        <MenuItem value="declined">Declined</MenuItem>
                        <MenuItem value="under_contract">Under Contract</MenuItem>
                        <MenuItem value="closed">Closed</MenuItem>
                        <MenuItem value="deal">Deals</MenuItem>
                        <MenuItem value="not_applicable">Not Applicable</MenuItem>
                      </Select>
                    </FormControl>
                  </td>
                  {statusFilter === 'not_applicable' && (
                    <td style={{ padding: cellPadding, fontSize: cellFontSize }}>
                      <FormControl size="small" sx={{ minWidth: 150 }}>
                        <Select
                          value={deal.naReason || ''}
                          displayEmpty
                          onChange={(e) => handleNaReasonChange(deal, e.target.value)}
                          sx={{
                            fontSize: 11,
                            '& .MuiSelect-select': { py: 0.5, px: 1 },
                            backgroundColor: deal.naReason ? '#f3f4f6' : '#fef2f2',
                            color: deal.naReason ? '#374151' : '#9ca3af',
                            fontWeight: 600,
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#6b7280' },
                          }}
                        >
                          <MenuItem value="" disabled><em>Select reason...</em></MenuItem>
                          {Object.entries(NA_REASONS).map(([val, label]) => (
                            <MenuItem key={val} value={val}>{label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </td>
                  )}
                  <td style={{ padding: cellPadding, fontSize: cellFontSize }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Chip
                        label={`${noteCount}`}
                        size="small"
                        variant="outlined"
                        onClick={() => setViewNotesDialog({ open: true, deal })}
                        sx={{ cursor: 'pointer', fontSize: 10, height: 20 }}
                      />
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => setNoteDialog({ open: true, deal })}
                        sx={{ minWidth: 0, p: 0.25, fontSize: 10 }}
                      >
                        +
                      </Button>
                    </div>
                  </td>
                  <td style={{ padding: cellPadding, whiteSpace: 'nowrap' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setEditDialog({ open: true, deal });
                        setNewStatus(deal.dealStatus || 'offer_sent_out');
                        setOfferAmount(deal.offerAmount ? String(deal.offerAmount) : '');
                        setEditNote('');
                        setEditAgentName(deal.agentName || '');
                        setEditAgentPhone(deal.agentPhone || '');
                        setEditAgentEmail(deal.agentEmail || '');
                        setEditNaReason(deal.naReason || '');
                      }}
                      sx={{ fontSize: 10, px: 1, py: 0.25, minWidth: 'auto', textTransform: 'none' }}
                    >
                      Edit
                    </Button>
                  </td>
                </tr>

                {/* Expandable Detail Row */}
                {isExpanded && (() => {
                  const addrForMap = deal.fullAddress || deal.address || '';
                  const addressQ = encodeURIComponent(addrForMap);
                  const sourceInfo = getSourceInfo(deal);
                  const amvSourceInfo = getAmvSourceInfo(deal);
                  const colCount = statusFilter === 'not_applicable' ? 14 : 13;

                  const lat = deal.lat;
                  const lng = deal.lng;

                  const streetSrc = GMAPS_KEY
                    ? (lat != null && lng != null
                      ? `https://www.google.com/maps/embed/v1/streetview?key=${GMAPS_KEY}&location=${lat},${lng}&heading=0&pitch=0&fov=90`
                      : `https://www.google.com/maps/embed/v1/streetview?key=${GMAPS_KEY}&location=${addressQ}&heading=0&pitch=0&fov=90`)
                    : null;

                  const mapSrc = GMAPS_KEY
                    ? `https://www.google.com/maps/embed/v1/place?key=${GMAPS_KEY}&q=${addressQ}&zoom=16`
                    : `https://www.google.com/maps?hl=en&q=${addressQ}&z=16&output=embed`;

                  const streetExternal = (lat != null && lng != null)
                    ? `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${lat},${lng}`
                    : `https://www.google.com/maps/search/?api=1&query=${addressQ}&layer=c`;

                  return (
                    <tr style={{ background: '#f0f4ff' }}>
                      <td colSpan={colCount} style={{ padding: 0 }}>
                        <div style={{ padding: '16px 20px', borderTop: '2px solid #2563eb', borderBottom: '1px solid #e5e7eb' }}>

                          {/* Row 1: Source + AMV Source chips */}
                          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 500 }}>Source:</span>
                              <Chip
                                label={sourceInfo.label}
                                size="small"
                                sx={{
                                  fontWeight: 600,
                                  fontSize: 11,
                                  backgroundColor: sourceInfo.bg,
                                  color: sourceInfo.color,
                                  border: `1px solid ${sourceInfo.border}`,
                                }}
                              />
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 500 }}>AMV from:</span>
                              {amvSourceInfo ? (
                                <Chip
                                  label={amvSourceInfo.label}
                                  size="small"
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: 11,
                                    backgroundColor: '#fff',
                                    color: amvSourceInfo.color,
                                    border: `1px solid ${amvSourceInfo.color}`,
                                  }}
                                />
                              ) : (
                                <span style={{ fontSize: 12, color: '#9ca3af' }}>Unknown</span>
                              )}
                            </div>
                          </div>

                          {/* Row 2: Street View / Map */}
                          <div style={{ width: '100%', borderRadius: 8, overflow: 'hidden', border: '1px solid #e5e7eb', marginBottom: 14 }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: 8, borderBottom: '1px solid #eee', background: '#fafafa' }}>
                              <div style={{ display: 'flex', gap: 8 }}>
                                <Button
                                  size="small"
                                  variant={detailViewMode === 'street' ? 'contained' : 'outlined'}
                                  onClick={() => setDetailViewMode('street')}
                                >
                                  Street View
                                </Button>
                                <Button
                                  size="small"
                                  variant={detailViewMode === 'map' ? 'contained' : 'outlined'}
                                  onClick={() => setDetailViewMode('map')}
                                >
                                  Map
                                </Button>
                              </div>
                              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                                <a
                                  href={streetExternal}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ fontSize: 12, color: '#2563eb', textDecoration: 'none', padding: '4px 6px' }}
                                  title="Open in Google Maps Street View"
                                >
                                  Google Maps
                                </a>
                                <a
                                  href={addressToWellsFargoUrl(addrForMap)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ fontSize: 12, color: '#059669', textDecoration: 'none', padding: '4px 6px' }}
                                  title="View agent details on Wells Fargo ComeHome"
                                >
                                  Agent Details
                                </a>
                                <a
                                  href="https://homevaluerealestatecenter.bankofamerica.com/"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={() => { navigator.clipboard.writeText(addrForMap || '').catch(() => {}); }}
                                  style={{ fontSize: 12, color: '#dc2626', textDecoration: 'none', padding: '4px 6px' }}
                                  title="Opens BofA & copies address to clipboard"
                                >
                                  BofA AMV
                                </a>
                              </div>
                            </div>
                            <div style={{ width: '100%', height: 260 }}>
                              {detailViewMode === 'street' ? (
                                streetSrc ? (
                                  <iframe
                                    key={`street-${deal._id}`}
                                    title="street-view"
                                    width="100%"
                                    height="100%"
                                    style={{ border: 0 }}
                                    loading="lazy"
                                    src={streetSrc}
                                    referrerPolicy="no-referrer-when-downgrade"
                                  />
                                ) : (
                                  <iframe
                                    key={`map-fallback-${deal._id}`}
                                    title="map"
                                    width="100%"
                                    height="100%"
                                    style={{ border: 0 }}
                                    loading="lazy"
                                    src={mapSrc}
                                    referrerPolicy="no-referrer-when-downgrade"
                                  />
                                )
                              ) : (
                                <iframe
                                  key={`map-${deal._id}`}
                                  title="map"
                                  width="100%"
                                  height="100%"
                                  style={{ border: 0 }}
                                  loading="lazy"
                                  src={mapSrc}
                                  referrerPolicy="no-referrer-when-downgrade"
                                />
                              )}
                            </div>
                          </div>

                          {/* Row 3: Property Details + Vendor Valuations */}
                          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                            {/* Property Details */}
                            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                              <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 500 }}>Property:</span>
                              <span style={{ fontSize: 13, color: '#111' }}>
                                {deal.beds != null ? `${deal.beds} Bed` : '—'} | {deal.baths != null ? `${deal.baths} Bath` : '—'} | {deal.sqft != null ? `${deal.sqft.toLocaleString()} Sq Ft` : '—'}
                              </span>
                            </div>

                            {/* Vendor Valuations */}
                            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                              <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 500 }}>Redfin Value:</span>
                              <span style={{ fontSize: 13, color: '#111' }}>
                                {fmt(deal.redfinAmv || deal.redfin_adjusted || deal.redfin_value)}
                              </span>
                            </div>
                            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                              <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 500 }}>BofA Value:</span>
                              <span style={{ fontSize: 13, color: '#111' }}>
                                {fmt(deal.bofa_value || deal.bofaAmv)}
                              </span>
                            </div>
                            {deal.zestimate && (
                              <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                                <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 500 }}>Zestimate:</span>
                                <span style={{ fontSize: 13, color: '#111' }}>{fmt(deal.zestimate)}</span>
                              </div>
                            )}
                          </div>

                        </div>
                      </td>
                    </tr>
                  );
                })()}
                </React.Fragment>
              );
            })}
            {deals.length === 0 && (
              <tr>
                <td colSpan={statusFilter === 'not_applicable' ? 14 : 13} style={{ padding: 24, textAlign: 'center', color: '#6b7280' }}>
                  {debouncedSearch ? (
                    <div>
                      <div style={{ marginBottom: 8 }}>
                        No results for "<strong>{debouncedSearch}</strong>" in {STATUS_LABELS[statusFilter]?.label} tab.
                      </div>
                      <div style={{ fontSize: 13, color: '#9ca3af' }}>
                        Try searching in other tabs or clear the search to see all deals.
                      </div>
                    </div>
                  ) : (
                    `No deals in Deal Status stage with status "${STATUS_LABELS[statusFilter]?.label}".`
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
          <Button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
          <span style={{ padding: '8px 16px', color: '#374151' }}>Page {page} of {totalPages}</span>
          <Button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
        </div>
      )}

      {/* Move Dialog */}
      <Dialog open={moveDialog.open} onClose={() => setMoveDialog({ open: false, deal: null, toStage: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>
          Move to {moveDialog.toStage === 'email_sent' ? 'Email Sent' : 'Follow Up'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <div style={{ color: '#374151', fontSize: 14 }}>
              <strong>Address:</strong> {moveDialog.deal?.fullAddress || moveDialog.deal?.address}
            </div>

            {moveDialog.toStage === 'follow_up' && (
              <TextField
                type="date"
                label="Follow Up Date"
                value={followUpDate}
                onChange={(e) => setFollowUpDate(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
                fullWidth
              />
            )}

            <TextField
              label="Note (optional)"
              multiline
              rows={3}
              value={moveNote}
              onChange={(e) => setMoveNote(e.target.value)}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMoveDialog({ open: false, deal: null, toStage: '' })}>Cancel</Button>
          <Button variant="contained" onClick={handleMove} disabled={moving}>
            {moving ? 'Moving...' : 'Move'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, deal: null })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: '#dc2626' }}>Delete Deal</DialogTitle>
        <DialogContent>
          <p style={{ margin: 0, color: '#374151' }}>
            Are you sure you want to delete this deal?
          </p>
          {deleteDialog.deal && (
            <p style={{ margin: '12px 0 0', fontWeight: 600, color: '#111827' }}>
              {deleteDialog.deal.fullAddress || deleteDialog.deal.address || 'Unknown address'}
            </p>
          )}
          <p style={{ margin: '12px 0 0', fontSize: 13, color: '#6b7280' }}>
            This action cannot be undone.
          </p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, deal: null })} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Status Dialog */}
      <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, deal: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Deal</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <div style={{ color: '#374151', fontSize: 14 }}>
              <strong>Address:</strong> {editDialog.deal?.fullAddress || editDialog.deal?.address}
            </div>

            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={newStatus}
                label="Status"
                onChange={(e) => setNewStatus(e.target.value)}
              >
                <MenuItem value="offer_sent_out">Offer Sent Out</MenuItem>
                <MenuItem value="counter">Counter</MenuItem>
                <MenuItem value="declined">Declined</MenuItem>
                <MenuItem value="under_contract">Under Contract</MenuItem>
                <MenuItem value="closed">Closed</MenuItem>
                <MenuItem value="deal">Deals</MenuItem>
                <MenuItem value="not_applicable">Not Applicable</MenuItem>
              </Select>
            </FormControl>

            {newStatus === 'not_applicable' && (
              <FormControl fullWidth>
                <InputLabel>N/A Reason</InputLabel>
                <Select
                  value={editNaReason}
                  label="N/A Reason"
                  onChange={(e) => setEditNaReason(e.target.value)}
                >
                  <MenuItem value=""><em>None</em></MenuItem>
                  {Object.entries(NA_REASONS).map(([val, label]) => (
                    <MenuItem key={val} value={val}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <TextField
              label="Offer Amount"
              value={offerAmount}
              onChange={(e) => setOfferAmount(e.target.value)}
              placeholder="e.g., 250000"
              fullWidth
              type="number"
            />

            {/* Agent Info */}
            <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 16, marginTop: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 12 }}>Agent Information</div>
              <Stack spacing={2}>
                <TextField
                  label="Agent Name"
                  value={editAgentName}
                  onChange={(e) => setEditAgentName(e.target.value)}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Agent Phone"
                  value={editAgentPhone}
                  onChange={(e) => setEditAgentPhone(e.target.value)}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Agent Email"
                  value={editAgentEmail}
                  onChange={(e) => setEditAgentEmail(e.target.value)}
                  fullWidth
                  size="small"
                  type="email"
                />
              </Stack>
            </div>

            <TextField
              label="Add Note (optional)"
              multiline
              rows={3}
              value={editNote}
              onChange={(e) => setEditNote(e.target.value)}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, deal: null })}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdateStatus} disabled={updating}>
            {updating ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Notes Dialog */}
      <Dialog open={viewNotesDialog.open} onClose={() => setViewNotesDialog({ open: false, deal: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Notes</DialogTitle>
        <DialogContent>
          <div style={{ color: '#374151', fontSize: 14, marginBottom: 16 }}>
            <strong>Address:</strong> {viewNotesDialog.deal?.fullAddress || viewNotesDialog.deal?.address}
          </div>
          {viewNotesDialog.deal?.followUpNotes?.length ? (
            <Stack spacing={2}>
              {viewNotesDialog.deal.followUpNotes.map((n, i) => (
                <div key={i} style={{ background: '#f9fafb', padding: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}>
                  <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>
                    {formatDateTime(n.createdAt)}
                  </div>
                  <div style={{ color: '#111', whiteSpace: 'pre-wrap' }}>{n.note}</div>
                </div>
              ))}
            </Stack>
          ) : (
            <div style={{ color: '#6b7280', textAlign: 'center', padding: 24 }}>No notes yet.</div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewNotesDialog({ open: false, deal: null })}>Close</Button>
          <Button
            variant="outlined"
            onClick={() => {
              setViewNotesDialog({ open: false, deal: null });
              setNoteDialog({ open: true, deal: viewNotesDialog.deal });
            }}
          >
            Add Note
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Note Dialog */}
      <Dialog open={noteDialog.open} onClose={() => setNoteDialog({ open: false, deal: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Add Note</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <div style={{ color: '#374151', fontSize: 14 }}>
              <strong>Address:</strong> {noteDialog.deal?.fullAddress || noteDialog.deal?.address}
            </div>
            <TextField
              label="Note"
              multiline
              rows={4}
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              fullWidth
              autoFocus
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoteDialog({ open: false, deal: null })}>Cancel</Button>
          <Button variant="contained" onClick={handleAddNote} disabled={addingNote || !newNote.trim()}>
            {addingNote ? 'Adding...' : 'Add Note'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast(t => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast.sev} variant="filled">{toast.msg}</Alert>
      </Snackbar>
    </div>
  );
}
